import { useRouter } from '@abyss/web/hooks/useRouter';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useContext, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { useCoverageType } from '../../hooks/useCoverageType/useCoverageType';
import { useLagoon } from '../../hooks/useLagoon';
import { usePrimaryCare } from '../../hooks/usePrimaryCare';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import {
  doChoosePCPStoreUpdate,
  getFuturePcpValues,
  isCnsRestrictedEditPcp,
} from '../../utils/primaryCare.utils';
import { getCurrentMemberData } from '../../utils/providerSearch.utils';
import {
  getLoggedInMember,
  getPolicyNumber,
  getReportingCode,
} from '../../utils/user.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { ANALYTICS_CONSTANTS } from '../CareTeamCard/CareTeamUtils';
import { CareTeamProviderCard } from '../CareTeamProviderCard';
import {
  CareCategories,
  ProviderTypes,
  ReverseCoverageTypesCodes,
} from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';
import { ThingsToConsiderModal } from '../ThingsToConsiderModal';
import { getFeatureFlag } from '../Utils';
import { addDaysToDate, dateFormatUTC } from '../Utils/datesUtils/formatDate';
import {
  isCnsMember,
  isEniMember,
  isIfpMember,
  isUmrMember,
} from '../Utils/memberUtils/memberUtils';
import {
  ChoosePCPButton,
  ChoosePCPCardContainer,
  ChoosePCPCnsOrIFP,
  InfoIconStyle,
  InfoPopoverContainerStyle,
  InfoPopoverListStyle,
} from './CareTeamEmptyState.styled';

export const ChoosePCPCard = ({ dependentSeqNbr, isPreEffective, pcpData }) => {
  const { data } = usePrimaryCare();
  const { primaryCare } = data;
  const lagoonData = useLagoon('ui-messaging')();
  const featureFlags = useLagoon('feature-flags')();
  const customMessages = useLagoon('custom-messages')();
  const allConfigs = useLagoon('config');
  const [showModal, setShowModal] = useState(false);
  const { portalData } = useContext(PortalContext);
  const { navigate } = useRouter();

  const displayThingsToConsiderForEni = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_THINGS_TO_CONSIDER_MODAL
  );

  const loggedInMemberInfo = getLoggedInMember();
  const currentMember = getCurrentMemberData(dependentSeqNbr);
  const { demographics, eligibility, lineOfBusiness, isGatedUser, population } =
    loggedInMemberInfo || {};
  const isUmr = isUmrMember(portalData?.portalName);

  const isCns = isCnsMember(lineOfBusiness);
  const isIfp = isIfpMember(population);

  const coverageType = useCoverageType();

  const memberPolicyNumber = getPolicyNumber(currentMember, coverageType);
  const memberReportingCode = getReportingCode(currentMember, coverageType);

  const memberPCPInfo = primaryCare?.find(
    ({ depSeqNbr }) => dependentSeqNbr === depSeqNbr
  )?.pcpInfo;

  const { pcpID = '', providerID, hasPCP, futurePCP } = memberPCPInfo || {};

  const sanitasPCP = allConfigs()?.find(
    (row) => row.key === 'SANITAS_PCPID'
  )?.value;

  const { isKelseySeyboldPlan, isSanitasPlan, editPcp } = currentMember;
  const isSanitasPlanMember =
    isSanitasPlan && memberPCPInfo?.futurePCP?.pcpID === sanitasPCP;
  const isKelseySeyboldPlanMember =
    isKelseySeyboldPlan &&
    memberPCPInfo?.futurePCP?.providerType?.toLowerCase() ===
      ProviderTypes.MEDICAL_GROUP.toLowerCase();

  const lockInIndicator = primaryCare?.[0]?.lockInIndicator;

  const hasCurrentPCP = memberPCPInfo != null && memberPCPInfo?.hasPCP === true;
  const hasFuturePCP =
    memberPCPInfo != null && memberPCPInfo?.futurePCP != null;

  const isCnsReportingCode =
    isCns &&
    isCnsRestrictedEditPcp(
      customMessages,
      memberReportingCode,
      memberPolicyNumber
    );

  const isSanitasOrKelseySeyboldOrLockInWithFuturePCP =
    (isSanitasPlanMember ||
      isKelseySeyboldPlanMember ||
      lockInIndicator ||
      isCnsReportingCode) &&
    !hasCurrentPCP &&
    hasFuturePCP;
  const isSanitasOrKelseySeyboldOrLockInWithoutPCP =
    (isSanitasPlanMember ||
      isKelseySeyboldPlanMember ||
      lockInIndicator ||
      isCnsReportingCode) &&
    !hasCurrentPCP &&
    !hasFuturePCP;
  const memberWithFuturePCP = !hasCurrentPCP && hasFuturePCP;
  const memberWithoutPCP = !hasCurrentPCP && !hasFuturePCP;

  const showThingsToConsider = () =>
    !isUmr && isEniMember(lineOfBusiness) && displayThingsToConsiderForEni;
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { setPCPSearchState } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      setPCPSearchState: state.setPCPSearchState,
      primarySpeciality: state.pcpSearchState.primarySpeciality,
    }))
  );

  const openThingsToConsiderModal = () => {
    setShowModal(true);
  };

  const onAddPCPClick = () => {
    const urlParams = doChoosePCPStoreUpdate(
      demographics?.dependentSeqNbr,
      { ...chipStore, chipValue: CareCategories.PRIMARY_CARE },
      setTypeaheadSearchStore,
      setPCPSearchState
    );
    navigate(`${ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path}${urlParams}`);
  };

  const noProviderTitle = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM.CARE_NO_PROVIDER_DESC,
  });

  const infoDetails = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM.CARE_CHOOSE_PCP_INFO,
  });

  const futurePcpMessage = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM.FUTURE_PCP_EFFECTIVE_DATE,
  });

  const noPcpMessage = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.PCP_NOT_REQUIRED_INFO,
  });

  const futurePCPMessage =
    isSanitasOrKelseySeyboldOrLockInWithFuturePCP || memberWithFuturePCP
      ? getFuturePcpValues(
          futurePcpMessage?.message || '',
          memberPCPInfo?.futurePCP
        )
      : '';

  const handleContinue = () => {
    setShowModal(false);
    onAddPCPClick();
  };

  const closeThingsToConsiderModal = () => {
    setShowModal(false);
  };

  const pcpWillBeAssignedSubscriber = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.PCP_WILL_BE_ASSIGNED_SUBSCRIBER,
  });

  const choosePcpSoonCareTeam = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CHOOSE_PCP_SOON_CARE_TEAM,
  });

  const choosePcpByCareTeam = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CHOOSE_PCP_BY_CARE_TEAM,
  });

  const enableNotRequiredPcpInfo = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NOT_REQUIRED_PCP_INFO
  );

  // Compute date and card condition for CNS/IFP
  const coverageMedical =
    eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
      (x) => x?.typeCode?.coverageTypeCode === 'M'
    );
  const coverageStartDate = dateFormatUTC(
    coverageMedical?.coverageEffDateRange?.startDate
  );
  const choosePcpByDate = addDaysToDate(new Date(coverageStartDate), 30);

  const dateDisplay = isCns ? (
    <>{choosePcpSoonCareTeam?.message}</>
  ) : (
    <>
      {choosePcpByCareTeam?.message} <strong>{choosePcpByDate}</strong>
    </>
  );
  const showYellowPCPRequiredCard =
    (!enableNotRequiredPcpInfo && isIfp && isPreEffective) ||
    (isCns && isGatedUser);

  const renderContent = () => {
    const infoData = infoDetails?.message?.split('<li>');
    if (!infoData || (Array.isArray(infoData) && infoData.length === 0))
      return null;
    return (
      <>
        <Text size="$sm">{infoData[0]}</Text>
        <ul style={InfoPopoverContainerStyle}>
          {infoData.map((item: string, index: number) => {
            if (index === 0) {
              return null; // Skip the first item as it is already rendered above
            }
            return (
              <li key={index} style={InfoPopoverListStyle}>
                {item}
              </li>
            );
          })}
        </ul>
      </>
    );
  };

  const choosePcpButton = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CHOOSE_PCP_BUTTON,
  });
  return (
    <React.Fragment>
      {pcpID && pcpData ? (
        <CareTeamProviderCard
          coverageType={ReverseCoverageTypesCodes.MEDICAL}
          data-auto-testid="care-team-provider-card"
          data-testid="care-team-provider-card"
          dependentSeqNbr={dependentSeqNbr}
          futurePCP={futurePCP}
          imgSrc={pcpData?.imageLocation}
          isPcP={hasPCP}
          isPreEffective={isPreEffective}
          isTieredPlan={
            pcpData?.providerLocations &&
            pcpData?.providerLocations[0]?.tieredProvider
          }
          isTieredProvider={pcpData?.isTieredProvider}
          llmCodes={pcpData?.llmCodes}
          organizationType={pcpData?.organizationType}
          pcpHasSingleLocation={pcpData?.providerLocations?.length === 1}
          professionalDesignation={pcpData?.primaryDegrees?.[0]}
          providerId={providerID}
          providerName={pcpData?.providerName || memberPCPInfo?.pcpName}
          providerType={pcpData?.providerType}
          showPcpLocation={pcpData?.providerLocations?.length > 1}
          snackCardProvider={pcpData}
          speciality={pcpData?.specialities
            ?.map((speciality) => speciality)
            .join(', ')}
        />
      ) : (
        <ChoosePCPCardContainer
          alignContent="center"
          alignItems="center"
          data-auto-testid={'care-choose-pcp-card'}
          data-testid={'care-choose-pcp-card'}
          direction="column"
        >
          <IconSymbol
            color="$greyBorderColor"
            data-auto-testid="care-account-circle"
            data-testid="care-account-circle"
            icon="account_circle"
            size="128"
          />
          {isSanitasOrKelseySeyboldOrLockInWithFuturePCP ||
          memberWithFuturePCP ? (
            <Text
              color="$black30"
              css={{
                textAlign: 'center',
                marginBottom: '10px',
                marginTop: '32px',
              }}
              data-auto-testid="future-pcp-effective-message"
              data-testid="future-pcp-effective-message"
              size="$md"
            >
              {futurePCPMessage}
            </Text>
          ) : isSanitasOrKelseySeyboldOrLockInWithoutPCP || memberWithoutPCP ? (
            <Text
              color="$black30"
              css={{
                textAlign: 'center',
                marginBottom: '10px',
                marginTop: '32px',
              }}
              data-auto-testid="no-pcp-message"
              data-testid="no-pcp-message"
              size="$md"
            >
              {noPcpMessage?.message}
            </Text>
          ) : showYellowPCPRequiredCard ? (
            <ChoosePCPCnsOrIFP
              data-auto-testid="choose-pcp-by-date"
              data-testid="choose-pcp-by-date"
            >
              {dateDisplay} {pcpWillBeAssignedSubscriber?.message ?? ''}
            </ChoosePCPCnsOrIFP>
          ) : (
            <Text
              color="$black30"
              css={{
                display: 'ruby',
                textAlign: 'center',
                marginBottom: '10px',
              }}
              data-auto-testid="care-team-empty-text"
              data-testid="care-team-empty-text"
              size="$md"
            >
              {noProviderTitle?.message}
              <Popover
                content={renderContent()}
                data-auto-testid={'care-info-icon-popover'}
                data-testid={'care-info-icon-popover'}
                title={infoDetails?.title}
              >
                <IconMaterial
                  css={InfoIconStyle}
                  data-auto-testid={'care-info-icon'}
                  data-testid={'care-info-icon'}
                  icon="info"
                  size={16}
                  variant="outlined"
                />
              </Popover>
            </Text>
          )}
          {!(
            isSanitasPlanMember ||
            isKelseySeyboldPlanMember ||
            lockInIndicator ||
            isCnsReportingCode
          ) &&
            editPcp && (
              <ChoosePCPButton
                data-auto-testid={'choose-pcp'}
                data-testid="choose-pcp-button"
                onClick={() => {
                  adobeLinkTrackEvent({
                    destinationUrl: '',
                    name: 'choose-pcp',
                    location: `body:${ANALYTICS_CONSTANTS.PCP_SNACK_CARD}`,
                    type: 'internal',
                  });
                  if (showThingsToConsider()) {
                    openThingsToConsiderModal();
                  } else {
                    onAddPCPClick();
                  }
                }}
              >
                {`${choosePcpButton?.message}`}
              </ChoosePCPButton>
            )}
          {showModal && (
            <ThingsToConsiderModal
              editPcpClicked={false}
              isOpen={showModal}
              onClose={closeThingsToConsiderModal}
              onContinue={handleContinue}
            />
          )}
        </ChoosePCPCardContainer>
      )}
    </React.Fragment>
  );
};
